<script setup>
import DeleteAffiliationAction from "@frontJs/components/actions/projectManagement/apikom/DeleteAffiliationAction.vue";
import { router } from "@inertiajs/vue3";
import ActionButton from "@js/components/ActionButton.vue";
import InputField from "@js/components/fields/InputField.vue";
import ListboxField from "@js/components/fields/ListboxField.vue";
import SwitchField from "@js/components/fields/SwitchField.vue";
import WysiwygField from "@js/components/fields/WysiwygField.vue";
import TabGroup from "@js/components/tabs/TabGroup.vue";
import TabPanel from "@js/components/tabs/TabPanel.vue";
import { goBack, useRoute } from "@js/utils/route";
import { Form } from "vee-validate";
import { reactive, ref } from "vue";
import { toast } from "vue3-toastify";
import { useI18n } from "vue-i18n";
import { string, object, boolean, array } from "yup";

const { t } = useI18n();

const types = [
  { value: "others", label: t("label.others") },
  { value: "opinions", label: t("label.opinions") },
  { value: "flow_manager", label: t("label.flowManager") },
  { value: "delivery", label: t("label.delivery") },
  { value: "netlinking", label: t("label.netlinking") },
  { value: "payment", label: t("label.payment") },
];

const { affiliation } = defineProps({
  affiliation: {
    required: false,
    type: Object,
  },
});

const { route } = useRoute();

const form = reactive({
  name: affiliation ? affiliation.name : "",
  url: affiliation ? affiliation.url : null,
  type: affiliation
    ? types.find((type) => type.value === affiliation.type)
    : types[0],
  isArchived: affiliation ? affiliation.isArchived : false,
  internalNotes: affiliation ? affiliation.internalNotes : null,
  contacts:
    affiliation && affiliation.contacts && affiliation.contacts.length > 0
      ? affiliation.contacts
      : [
          {
            id: null,
            name: "",
            email: "",
            phone: "",
          },
        ],
});

const isSubmitting = ref(false);

const selectedTab = ref(0);

const validationSchema = object({
  name: string().required(t("error.required")),
  url: string().url(t("error.url")).nullable(),
  type: object({
    value: string().required(t("error.required")),
    label: string().required(t("error.required")),
  }).required(t("error.required")),
  isArchived: boolean().required(t("error.required")),
  internalNotes: string().nullable(),
  contacts: array().of(
    object({
      name: string().required(t("error.required")),
      email: string().email(t("error.email")).nullable(),
      phone: string().nullable(),
    }),
  ),
});

function changeTab(index) {
  selectedTab.value = index;
}

function saveAction() {
  let error;

  isSubmitting.value = true;

  fetch(
    affiliation
      ? route("front.projectManagement.apikom.postAffiliation", {
          affiliation: affiliation.id,
        })
      : route("front.projectManagement.apikom.postAffiliation"),
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-TOKEN": document
          .querySelector('meta[name="csrf-token"]')
          .getAttribute("content"),
      },
      body: JSON.stringify({
        name: form.name,
        url: form.url,
        type: form.type.value,
        isArchived: form.isArchived,
        internalNotes: form.internalNotes,
        contacts: form.contacts,
      }),
    },
  )
    .then((response) => {
      error = !response.ok;

      return response.json();
    })
    .then((data) => {
      if (error) {
        toast.error(data.message, { position: "top-right" });
      } else {
        toast.success(t("toast.successfullySaved"), {
          position: "top-right",
        });

        router.visit(route("front.projectManagement.apikom.affiliations"));
      }
    })
    .finally(() => {
      isSubmitting.value = false;
    })
    .catch((error) => {
      toast.error(error.message, { position: "top-right" });
      router.visit(route("front.projectManagement.apikom.affiliations"));
    });
}

function deleteCallback() {
  toast.success(t("toast.successfullyDeleted"), { position: "top-right" });
  router.visit(route("front.projectManagement.apikom.affiliations"));
}
</script>

<template>
  <Form
    v-slot="{ errors }"
    :validation-schema="validationSchema"
    @submit="saveAction"
  >
    <TabGroup
      :selected-tab="selectedTab"
      :change-tab="changeTab"
      :tabs="{
        information: t('title.information'),
        internalNotes: t('title.internalNotes'),
      }"
      :replace="false"
    >
      <template #panels>
        <TabPanel>
          <div class="grid grid-cols-1 gap-x-8 md:grid-cols-2 lg:grid-cols-4">
            <InputField
              v-model="form.name"
              :label="t('label.name')"
              field-name="name"
              field-id="name"
              type="text"
              required
            />

            <InputField
              v-model="form.url"
              :label="t('label.url')"
              field-name="url"
              field-id="url"
              type="url"
            />

            <ListboxField
              v-model="form.type"
              field-id="type"
              field-name="type"
              :label="t('label.type')"
              :options="types"
              required
            >
              <template #buttonContent>
                {{ form.type.label }}
              </template>

              <template #optionContent="{ option }">
                {{ option.label }}
              </template>
            </ListboxField>
          </div>

          <hr class="h-0.25 my-4 w-full bg-gray-200" />

          <div class="grid grid-cols-1 gap-x-8 md:grid-cols-2 lg:grid-cols-4">
            <SwitchField
              v-model="form.isArchived"
              :label="t('label.archived')"
              field-name="isArchived"
              field-id="isArchived"
            />
          </div>

          <hr class="h-0.25 my-4 w-full bg-gray-200" />

          <div
            v-for="(contact, index) in form.contacts"
            :key="index"
            class="grid grid-cols-1 gap-x-8 md:grid-cols-2 lg:grid-cols-4"
          >
            <InputField
              v-model="form.contacts[index].name"
              :label="t('label.contactName', { index: index + 1 })"
              :field-name="'contacts[' + index + '].name'"
              :field-id="'contacts[' + index + '].name'"
              type="text"
              required
            />

            <InputField
              v-model="contact.email"
              :label="t('label.contactEmail', { index: index + 1 })"
              :field-name="'contacts[' + index + '].email'"
              :field-id="'contacts[' + index + '].email'"
              type="email"
            />

            <InputField
              v-model="contact.phone"
              :label="t('label.contactPhone', { index: index + 1 })"
              :field-name="'contacts[' + index + '].phone'"
              :field-id="'contacts[' + index + '].phone'"
              type="tel"
            />

            <div class="mb-4 flex items-end justify-start">
              <ActionButton :callback="() => form.contacts.splice(index, 1)">
                {{ t("button.delete") }}
              </ActionButton>
            </div>
          </div>

          <ActionButton
            :callback="
              () =>
                form.contacts.push({ id: null, name: '', email: '', phone: '' })
            "
          >
            {{ t("button.add") }}
          </ActionButton>
        </TabPanel>

        <TabPanel>
          <WysiwygField
            v-model="form.internalNotes"
            :label="t('label.internalNotes')"
            field-name="internalNotes"
            field-id="internalNotes"
          />
        </TabPanel>
      </template>
    </TabGroup>

    <div
      class="flex flex-wrap items-center gap-2 rounded-b border border-gray-200 bg-white p-6"
    >
      <ActionButton type="transparent" :callback="() => goBack()">
        {{ t("button.back") }}
      </ActionButton>

      <DeleteAffiliationAction
        v-if="affiliation"
        :affiliation="affiliation"
        :callback="deleteCallback"
      >
        <template #button="{ setIsOpen }">
          <ActionButton type="secondary" :callback="() => setIsOpen(true)">
            {{ t("button.delete") }}
          </ActionButton>
        </template>
      </DeleteAffiliationAction>

      <ActionButton
        submit
        :disabled="Object.keys(errors).length > 0 || isSubmitting"
        :loading="isSubmitting"
      >
        <template v-if="!isSubmitting">
          {{ t("button.save") }}
        </template>

        <template v-else>
          {{ t("button.saveInProgress") }}
        </template>
      </ActionButton>

      <div v-if="Object.keys(errors).length > 0" class="text-sm text-red-500">
        {{ t("error.formHasErrors") }}
      </div>
    </div>
  </Form>
</template>
